import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  AlertCircle, RefreshCw, Search, TrendingUp, Clock, AlertTriangle, CircleDollarSign, ChevronRight,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatIDR = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');
const formatShort = (n) => {
  const v = Number(n || 0);
  if (v >= 1e9) return 'Rp ' + (v / 1e9).toFixed(1) + 'M';
  if (v >= 1e6) return 'Rp ' + (v / 1e6).toFixed(1) + 'jt';
  if (v >= 1e3) return 'Rp ' + (v / 1e3).toFixed(0) + 'rb';
  return 'Rp ' + v;
};

const BUCKET_META = {
  in_debt_grace: {
    label: 'Masa Tenggang',
    desc: 'Ditandai berhutang oleh admin — dalam masa tenggang, belum diisolir sampai deadline.',
    tone: 'from-amber-500/10 to-amber-500/5 border-amber-300',
    icon: Clock,
    color: 'text-amber-700 dark:text-amber-300',
  },
  '0_30': {
    label: '0-30 Hari',
    desc: 'Piutang < 30 hari sejak jatuh tempo.',
    tone: 'from-sky-500/10 to-sky-500/5 border-sky-300',
    icon: TrendingUp,
    color: 'text-sky-700 dark:text-sky-300',
  },
  '30_60': {
    label: '30-60 Hari',
    desc: 'Piutang 30-60 hari sejak jatuh tempo.',
    tone: 'from-orange-500/10 to-orange-500/5 border-orange-300',
    icon: AlertTriangle,
    color: 'text-orange-700 dark:text-orange-300',
  },
  '60_plus': {
    label: '60+ Hari',
    desc: 'Piutang > 60 hari (kritis).',
    tone: 'from-red-500/10 to-red-500/5 border-red-300',
    icon: AlertCircle,
    color: 'text-red-700 dark:text-red-300',
  },
};

const Receivables = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  // 2026-07-31: Support ?bucket=<key> URL param for deep-linking (e.g., from Masa Tenggang counter).
  const [searchParams] = useSearchParams();
  const initialBucket = (() => {
    const b = searchParams.get('bucket');
    const valid = ['in_debt_grace', '0_30', '30_60', '60_plus'];
    return valid.includes(b) ? b : '0_30';
  })();
  const [activeBucket, setActiveBucket] = useState(initialBucket);
  const [search, setSearch] = useState('');
  // 2026-07-30: pagination
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/v2/reports/receivables`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Gagal memuat laporan piutang');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const buckets = data?.buckets || { in_debt_grace: [], '0_30': [], '30_60': [], '60_plus': [] };
  const totals = data?.totals || { in_debt_grace: 0, '0_30': 0, '30_60': 0, '60_plus': 0 };
  const grandTotal = data?.grand_total || 0;
  const grandCount = data?.grand_count || 0;

  const rows = useMemo(() => {
    // If user is searching, aggregate across ALL buckets and attach the bucket key
    // so the user can find any customer regardless of which bucket they belong to.
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const merged = [];
      ['in_debt_grace', '0_30', '30_60', '60_plus'].forEach((k) => {
        (buckets[k] || []).forEach((r) => merged.push({ ...r, __bucket: k }));
      });
      return merged.filter((r) =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.account_number || '').toLowerCase().includes(q) ||
        (r.pppoe_username || '').toLowerCase().includes(q) ||
        (r.phone || '').toLowerCase().includes(q) ||
        (r.branch_name || '').toLowerCase().includes(q)
      );
    }
    // Otherwise show only the active bucket rows
    return (buckets[activeBucket] || []).map((r) => ({ ...r, __bucket: activeBucket }));
  }, [buckets, activeBucket, search]);

  // 2026-07-30: Reset to page 1 whenever bucket/search/itemsPerPage changes
  useEffect(() => { setCurrentPage(1); }, [activeBucket, search, itemsPerPage]);

  // Paginated slice
  const totalRows = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * itemsPerPage;
  const endIdx = Math.min(startIdx + itemsPerPage, totalRows);
  const paginatedRows = rows.slice(startIdx, endIdx);

  // 2026-07-30: When user clicks "Buka" on a receivable row, land them on the
  // Customers LIST page (not the detail page) with the target customer
  // pre-searched, scrolled into view and flash-highlighted. This uses the
  // sessionStorage protocol already implemented in Customers.jsx.
  const handleOpenInList = (row) => {
    if (!row || !row.id) return;
    // Determine which tab the customer belongs to based on payment_type / pppoe_username.
    // PPPoE is the default (base route). Hotspot & DHCP have their own routes.
    let targetPath = '/app/customers';
    let stateKey = 'customers_list_state_pppoe';
    const pt = (row.payment_type || '').toLowerCase();
    if (pt === 'hotspot' || pt === 'voucher') {
      targetPath = '/app/customers/hotspot';
      stateKey = 'customers_list_state_hotspot';
    } else if (pt === 'static' || pt === 'static_ip' || pt === 'dhcp') {
      targetPath = '/app/customers/dhcp';
      stateKey = 'customers_list_state_dhcp';
    }
    try {
      sessionStorage.setItem(stateKey, JSON.stringify({
        searchTerm: row.account_number || row.name || '',
        viewedCustomerId: row.id,
      }));
    } catch (_e) { /* non-fatal */ }
    navigate(targetPath);
  };

  return (
    <div className="space-y-4 p-3 sm:p-4 lg:p-6" data-testid="receivables-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <CircleDollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-500" />
            Laporan Piutang
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Aging report piutang customer — dikelompokkan berdasarkan usia tunggakan.
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" data-testid="btn-refresh-receivables">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Grand total banner */}
      <Card className="border-2 border-indigo-200 dark:border-indigo-800 bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-950/50 dark:to-slate-900">
        <CardContent className="py-3 sm:py-5">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-indigo-600 dark:text-indigo-400 font-semibold">Total Piutang</p>
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 dark:text-slate-100 mt-1 break-all" data-testid="receivables-grand-total">
                {formatIDR(grandTotal)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total Customer</p>
              <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="receivables-grand-count">{grandCount}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bucket cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
        {['in_debt_grace', '0_30', '30_60', '60_plus'].map((key) => {
          const meta = BUCKET_META[key];
          const Icon = meta.icon;
          const count = (buckets[key] || []).length;
          const total = totals[key] || 0;
          const isActive = activeBucket === key;
          return (
            <button
              key={key}
              onClick={() => setActiveBucket(key)}
              data-testid={`bucket-card-${key}`}
              className={`relative text-left rounded-lg border p-2.5 transition-all bg-gradient-to-br ${meta.tone} ${isActive ? 'ring-2 ring-indigo-500 dark:ring-indigo-400 shadow-sm' : 'hover:shadow-sm'}`}
            >
              <div className="flex items-center justify-between">
                <Icon className={`w-4 h-4 ${meta.color}`} />
                {isActive && <ChevronRight className="w-3.5 h-3.5 text-indigo-500" />}
              </div>
              <p className={`text-[10px] uppercase tracking-wider font-semibold mt-1 ${meta.color}`}>{meta.label}</p>
              <p className="text-base lg:text-lg font-bold text-slate-900 dark:text-slate-100 mt-0.5 leading-tight">{formatShort(total)}</p>
              <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-0.5">{count} customer</p>
            </button>
          );
        })}
      </div>

      {/* Details */}
      <Card>
        <CardHeader className="pb-3">
          {/* 2026-07-30: Tampilkan entri selector (matches Pelanggan list UX) */}
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <span>Tampilkan entri</span>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="h-8 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                data-testid="receivables-items-per-page"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
              </select>
              <span>entri</span>
            </div>
            <div className="text-xs text-slate-500">
              Menampilkan {totalRows > 0 ? (startIdx + 1) : 0}-{endIdx} dari {totalRows}
            </div>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <span className={`inline-block w-2 h-6 rounded ${BUCKET_META[activeBucket].color.replace('text-','bg-')} opacity-70`} />
                {search.trim() ? `Hasil pencarian: "${search.trim()}"` : BUCKET_META[activeBucket].label}
              </CardTitle>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {search.trim() ? `Mencari di SEMUA kategori — ${rows.length} customer cocok.` : BUCKET_META[activeBucket].desc}
              </p>
            </div>
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                className="pl-9 pr-9"
                placeholder="Cari nama / akun / PPPoE / cabang..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="receivables-search-input"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition"
                  aria-label="Bersihkan pencarian"
                  data-testid="receivables-search-clear"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center text-sm text-slate-500">Memuat data...</div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-500" data-testid="receivables-empty">
              Tidak ada customer di kategori ini.
            </div>
          ) : (
            <>
            {/* 2026-07-31: MOBILE card list — hanya tampil di < sm (640px) */}
            <div className="lg:hidden divide-y divide-slate-200 dark:divide-slate-700">
              {paginatedRows.map((r, i) => {
                const absIdx = startIdx + i;
                let ddFmt = null;
                try { if (r.debt_deadline) ddFmt = format(parseISO(r.debt_deadline), 'dd MMM yyyy', { locale: idLocale }); } catch (e) {}
                let ndFmt = null;
                try { if (r.next_due_date) ndFmt = format(parseISO(r.next_due_date), 'dd MMM yyyy', { locale: idLocale }); } catch (e) {}
                return (
                  <div key={r.id} className="p-3" data-testid={`receivable-card-${r.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs text-slate-400">#{absIdx + 1}</div>
                        <div className="font-semibold text-sm truncate">{r.name || '-'}</div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {r.account_number || '-'}{r.pppoe_username ? ` • ${r.pppoe_username}` : ''}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-bold text-sm text-slate-900 dark:text-slate-100">{formatIDR(r.monthly_price)}</div>
                        <Badge variant={r.days_overdue > 60 ? 'destructive' : r.days_overdue > 30 ? 'default' : 'secondary'} className="text-[10px] mt-0.5">
                          {r.days_overdue} hari
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 text-[11px] text-slate-600 dark:text-slate-400 mt-1">
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="truncate">📍 {r.branch_name || '-'}</span>
                        <span>📅 {ndFmt || '-'}</span>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {(r.__bucket || activeBucket) === 'in_debt_grace' ? (
                          <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px]">
                            🕐 s/d {ddFmt || '-'}
                          </Badge>
                        ) : r.is_isolated ? (
                          <Badge variant="destructive" className="text-[10px]">ISOLIR</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] text-slate-600">BELUM BAYAR</Badge>
                        )}
                        <button
                          type="button"
                          onClick={() => handleOpenInList(r)}
                          className="text-[11px] text-indigo-600 hover:text-indigo-800 font-medium"
                          data-testid={`receivable-open-mobile-${r.id}`}
                        >Buka →</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* DESKTOP table — hanya tampil ≥ sm */}
            <div className="overflow-x-auto hidden lg:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">#</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Cabang</TableHead>
                    <TableHead>Jatuh Tempo</TableHead>
                    <TableHead className="text-center">Umur (Hari)</TableHead>
                    <TableHead className="text-right">Nilai</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedRows.map((r, i) => {
                    const absIdx = startIdx + i;
                    let ddFmt = null;
                    try {
                      if (r.debt_deadline) ddFmt = format(parseISO(r.debt_deadline), 'dd MMM yyyy', { locale: idLocale });
                    } catch {}
                    let ndFmt = null;
                    try {
                      if (r.next_due_date) ndFmt = format(parseISO(r.next_due_date), 'dd MMM yyyy', { locale: idLocale });
                    } catch {}
                    return (
                      <TableRow key={r.id} data-testid={`receivable-row-${r.id}`}>
                        <TableCell className="text-slate-500">{absIdx + 1}</TableCell>
                        <TableCell>
                          <div className="font-medium">{r.name || '-'}</div>
                          <div className="text-xs text-slate-500">
                            {r.account_number || '-'}
                            {r.pppoe_username ? ` • ${r.pppoe_username}` : ''}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{r.branch_name || '-'}</TableCell>
                        <TableCell className="text-sm">{ndFmt || '-'}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant={r.days_overdue > 60 ? 'destructive' : r.days_overdue > 30 ? 'default' : 'secondary'}>
                            {r.days_overdue} hari
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold text-slate-900 dark:text-slate-100">
                          {formatIDR(r.monthly_price)}
                        </TableCell>
                        <TableCell>
                          {(r.__bucket || activeBucket) === 'in_debt_grace' ? (
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200">
                              🕐 MASA TENGGANG s/d {ddFmt || '-'}
                            </Badge>
                          ) : r.is_isolated ? (
                            <Badge variant="destructive">ISOLIR</Badge>
                          ) : (
                            <Badge variant="outline" className="text-slate-600">BELUM BAYAR</Badge>
                          )}
                          {search.trim() && (
                            <span className="ml-2 text-[10px] text-slate-400" title={`Bucket: ${BUCKET_META[r.__bucket || activeBucket].label}`}>
                              [{BUCKET_META[r.__bucket || activeBucket].label}]
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <button
                            type="button"
                            onClick={() => handleOpenInList(r)}
                            className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
                            data-testid={`receivable-open-${r.id}`}
                            title={`Buka ${r.name} di list Pelanggan`}
                          >
                            Buka →
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            </>
          )}
          {/* 2026-07-30: Pagination footer */}
          {!loading && totalRows > 0 && (
            <div className="flex items-center justify-between flex-wrap gap-3 px-4 py-3 border-t border-slate-100 dark:border-slate-800">
              <div className="text-xs text-slate-600 dark:text-slate-400">
                Halaman <span className="font-semibold text-slate-900 dark:text-slate-100">{safePage}</span> dari <span className="font-semibold text-slate-900 dark:text-slate-100">{totalPages}</span>
                {' '}({totalRows} customer)
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setCurrentPage(1)}
                  disabled={safePage === 1}
                  className="h-8 px-2 rounded border border-slate-300 dark:border-slate-700 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="rcv-page-first"
                >
                  «
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="h-8 px-2 sm:px-3 rounded border border-slate-300 dark:border-slate-700 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="rcv-page-prev"
                  aria-label="Sebelumnya"
                >
                  <span className="sm:hidden">‹</span>
                  <span className="hidden sm:inline">Sebelumnya</span>
                </button>
                {(() => {
                  // Show a compact numeric window around current page
                  const around = (typeof window !== "undefined" && window.innerWidth < 640) ? 1 : 2;
                  const pages = [];
                  const first = Math.max(1, safePage - around);
                  const last = Math.min(totalPages, safePage + around);
                  if (first > 1) pages.push(1);
                  if (first > 2) pages.push('...');
                  for (let p = first; p <= last; p++) pages.push(p);
                  if (last < totalPages - 1) pages.push('...');
                  if (last < totalPages) pages.push(totalPages);
                  return pages.map((p, idx) => p === '...'
                    ? <span key={`ell-${idx}`} className="px-1 text-slate-400">...</span>
                    : (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setCurrentPage(p)}
                        className={`h-8 min-w-[32px] px-2 rounded border text-xs font-medium ${
                          p === safePage
                            ? 'bg-indigo-500 text-white border-indigo-500'
                            : 'border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'
                        }`}
                        data-testid={`rcv-page-${p}`}
                      >
                        {p}
                      </button>
                    )
                  );
                })()}
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="h-8 px-2 sm:px-3 rounded border border-slate-300 dark:border-slate-700 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="rcv-page-next"
                  aria-label="Selanjutnya"
                >
                  <span className="sm:hidden">›</span>
                  <span className="hidden sm:inline">Selanjutnya</span>
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={safePage === totalPages}
                  className="h-8 px-2 rounded border border-slate-300 dark:border-slate-700 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="rcv-page-last"
                >
                  »
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Receivables;
